package com.swetdot.swetgram.ui.legal

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalUriHandler
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.SwetDestructiveTextButton
import com.swetdot.swetgram.ui.design.SwetErrorBanner
import com.swetdot.swetgram.ui.design.SwetLoading
import com.swetdot.swetgram.ui.design.SwetPrimaryButton
import com.swetdot.swetgram.ui.design.SwetScreenTitle
import com.swetdot.swetgram.ui.design.SwetSecondaryButton
import com.swetdot.swetgram.ui.design.SwetSkeleton
import com.swetdot.swetgram.ui.design.SwetTextButton
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens

/**
 * Neutral holding surface while the server decides which side of the legal gate this account is
 * on. It deliberately contains no age or acceptance copy: an already-accepted user must not see
 * that screen flash merely because the status request has not completed yet.
 */
@Composable
internal fun LegalStatusScreen(
    state: LegalUiState,
    onRetry: () -> Unit,
    onSignOut: () -> Unit,
    onRequestDeletion: () -> Unit,
    onDismissError: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val colors = SwetgramThemeTokens.colors

    if (state.error == null) {
        SwetLoading(
            modifier = modifier
                .fillMaxSize()
                .background(colors.backgroundDeep)
                .statusBarsPadding()
                .navigationBarsPadding(),
            label = "Checking your account…",
            contentDescription = "Checking account status",
        )
        return
    }

    Column(
        modifier
            .fillMaxSize()
            .background(colors.backgroundDeep)
            .statusBarsPadding()
            .navigationBarsPadding()
            .padding(horizontal = Space.screenMargin, vertical = Space.four),
        verticalArrangement = Arrangement.Center,
    ) {
        SwetErrorBanner(state.error, onRetry = onRetry, onDismiss = onDismissError)
        Spacer(Modifier.height(Space.three))
        SwetSecondaryButton("Sign out", onClick = onSignOut, enabled = !state.busy)
        Spacer(Modifier.height(Space.two))
        if (state.deletionScheduled) {
            Text(
                "Account deletion is scheduled. You can cancel it by signing in before the recovery period ends.",
                style = MaterialTheme.typography.bodyMedium,
                color = colors.textSecondary,
            )
        } else {
            SwetDestructiveTextButton(
                text = "Delete account",
                onClick = onRequestDeletion,
                enabled = !state.busy,
            )
        }
    }
}

@Composable
fun LegalScreen(
    state: LegalUiState,
    onAccept: () -> Unit,
    onRetry: () -> Unit,
    onSignOut: () -> Unit,
    onRequestDeletion: () -> Unit,
    onDismissError: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val colors = SwetgramThemeTokens.colors
    val uri = LocalUriHandler.current
    val documents = state.status?.documents

    Column(
        modifier
            .fillMaxSize()
            .background(colors.backgroundDeep)
            .statusBarsPadding()
            .navigationBarsPadding()
            .verticalScroll(rememberScrollState())
            .padding(horizontal = Space.screenMargin, vertical = Space.four),
    ) {
        SwetScreenTitle("Before you continue")
        Spacer(Modifier.height(Space.two))
        Text(
            "Swetgram is for people aged 16 and over. Please read and accept the current Terms and Privacy Policy.",
            style = MaterialTheme.typography.bodyLarge,
            color = colors.textSecondary,
        )
        Spacer(Modifier.height(Space.three))

        if (state.loading || documents == null) {
            SwetSkeleton(modifier = Modifier.height(Space.six))
            Spacer(Modifier.height(Space.two))
            SwetSecondaryButton("Try again", onClick = onRetry, enabled = !state.busy)
        } else {
            SwetTextButton("Terms of Service", onClick = { uri.openUri(documents.termsUrl) })
            SwetTextButton("Privacy Policy", onClick = { uri.openUri(documents.privacyUrl) })
            SwetTextButton("Community Guidelines", onClick = { uri.openUri(documents.communityGuidelinesUrl) })
            Spacer(Modifier.height(Space.three))
            SwetPrimaryButton(
                text = "I am 16+ and accept",
                onClick = onAccept,
                loading = state.busy,
            )
        }

        state.error?.let {
            Spacer(Modifier.height(Space.two))
            SwetErrorBanner(it, onRetry = onRetry, onDismiss = onDismissError)
        }

        Spacer(Modifier.height(Space.four))
        SwetSecondaryButton("Sign out", onClick = onSignOut, enabled = !state.busy)
        Spacer(Modifier.height(Space.two))
        if (state.deletionScheduled) {
            Text(
                "Account deletion is scheduled. You can cancel it by signing in before the recovery period ends.",
                style = MaterialTheme.typography.bodyMedium,
                color = colors.textSecondary,
            )
        } else {
            SwetDestructiveTextButton(
                text = "Delete account",
                onClick = onRequestDeletion,
                enabled = !state.busy,
            )
        }
    }
}
